import { useMutation } from '@apollo/client/react';
import { Button, Card, Flex, Form, Input, message, Row } from 'antd';
import { findIndex, get, isNaN, isString, map, trim } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../../../AppContext';
import { ForwardArrow, ProjectIcon } from '../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../common/CommonUseLazyQuery';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  DEFAULTDATEFORMAT,
  PROPERTY_TYPES,
  ROUTES,
  TAB_KEYS,
  TIME_24_HOUR_FORMAT,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import {
  calculateFieldValue,
  formatNumericInput,
  sanitizeFieldName,
  toCamelCase,
} from '../../../../../../common/utils';
import CanPerform from '../../../../../../components/CanPerform';
import {
  CREATE_NEW_SET_PROPERTY,
  SAVE_REGISTER_LOG_TO_DRAFT,
} from '../../../../graphql/Mutation';
import { GET_SINGLE_PROJECT } from '../../../../graphql/Queries';
import FieldIcon from './FieldIcon';
import RenderFields from './RenderFields';
import UnsavedChangesPrompt from './registerDetails/UnsavedChangesPrompt';

const renderPropertyUi = (
  data,
  form,
  isOptionsFocused,
  setIsOptionsFocused,
  isMultiSelectOptionsFocused,
  setIsMultiSelectOptionsFocused,
  isDateFocused,
  setIsDateFocused,
  isTimeFocused,
  setIsTimeFocused,
  handleFieldChange,
  setEqcLabels,
  nomenclatureStatus,
) => {
  // Filter out deleted fields
  const activeData = data?.filter((item) => !item?.deletedAt);
  const transformedData = map(activeData, (item) => ({
    ...item,
    index: sanitizeFieldName(item?.name), // Add index property for calculateFieldValue
  }));

  return map(transformedData, (item) => {
    const fieldName = sanitizeFieldName(item?.name);
    const formFieldName = `0_${fieldName}`; // Use 0_ prefix for compatibility with calculateFieldValue

    switch (item?.type) {
      case PROPERTY_TYPES.NUMERIC:
        return (
          <Form.Item
            label={
              <span className="d-flex align-center white-space-pre-line text-break">
                <div className="mr-5">
                  <FieldIcon fieldType={item?.type} />
                </div>
                {item?.name}
              </span>
            }
            name={formFieldName}
            rules={[
              {
                required: item?.isRequired,
                message: 'This field is required!',
              },
              {
                max: 15,
                message: 'Maximum 15 digits are allowed',
              },
              {
                validator: (_, value) => {
                  if (!value || value === '' || value === '-') {
                    return Promise?.resolve();
                  }
                  const numericValue = Number(value);
                  if (Number?.isNaN(numericValue)) {
                    return Promise?.reject(
                      new Error('Please enter a valid number'),
                    );
                  }
                  return Promise?.resolve();
                },
              },
            ]}
            normalize={(value) => formatNumericInput(value)}
          >
            <Input
              placeholder="Enter number"
              inputMode="decimal"
              allowClear
              id={`add-log-numeric-${fieldName}`}
              onChange={(e) => {
                const formattedValue = formatNumericInput(e?.target?.value);
                handleFieldChange(formFieldName, formattedValue);
              }}
            />
          </Form.Item>
        );
      default:
        return (
          <Form.Item key={formFieldName} noStyle shouldUpdate>
            {() => (
              <RenderFields
                fieldType={item?.type}
                fieldIndex={formFieldName}
                fieldName={item?.name}
                fieldIsRequired={item?.isRequired}
                onDateFocused={() => setIsDateFocused(fieldName)}
                onDateOpenChange={(e) => {
                  if (!e) {
                    setIsDateFocused();
                  }
                }}
                onDateChange={(value) => {
                  handleFieldChange(formFieldName, value);
                }}
                dateOpen={isDateFocused === fieldName}
                options={map(item?.options, (option) => ({
                  label: option,
                  value: option,
                }))}
                onOptionsFocus={() => setIsOptionsFocused(fieldName)}
                onOptionBlur={() => setIsOptionsFocused(null)}
                onOptionChange={() => setIsOptionsFocused(null)}
                onMultiSelectOpenChange={(open) => {
                  if (!open) {
                    setIsOptionsFocused(null);
                  } else {
                    setIsOptionsFocused(fieldName);
                  }
                }}
                optionsOpen={isOptionsFocused === fieldName}
                onMultiSelectFocus={() =>
                  setIsMultiSelectOptionsFocused(fieldName)
                }
                onMultiSelectBlur={() => setIsMultiSelectOptionsFocused(null)}
                multiSelectOpen={isMultiSelectOptionsFocused === fieldName}
                timeOpen={isTimeFocused === fieldName}
                onTimeFocused={() => setIsTimeFocused(fieldName)}
                onTimeOpenChange={(e) => {
                  if (!e) {
                    setIsTimeFocused();
                  }
                }}
                onTimeChange={(value) => {
                  handleFieldChange(formFieldName, value);
                }}
                calculateFieldValue={() =>
                  calculateFieldValue(formFieldName, form, transformedData)
                }
                onCalculationChange={(e) =>
                  handleFieldChange(formFieldName, e?.target?.value)
                }
                onCalculationBlur={() => {
                  const calculatedValue = calculateFieldValue(
                    formFieldName,
                    form,
                    transformedData,
                  );
                  form?.setFieldValue(formFieldName, calculatedValue);
                }}
                onEqcChange={(ids, options) => {
                  handleFieldChange(formFieldName, ids);
                  if (options && Array?.isArray(options)) {
                    const labels = options?.map((opt) => opt?.label)?.join(',');
                    setEqcLabels((prev) => ({
                      ...prev,
                      [fieldName]: labels,
                    }));
                  } else {
                    setEqcLabels((prev) => ({
                      ...prev,
                      [fieldName]: '',
                    }));
                  }
                }}
                nomenclatureStatus={nomenclatureStatus}
                popupContainer={(trigger) => trigger?.parentElement}
              />
            )}
          </Form.Item>
        );
    }
  });
};

const AddSetsProperty = () => {
  const { getCurrentUser, getCurrentProjectDetail, dispatch } =
    useContext(AppContext);
  const currentUserId = getCurrentUser()?.id || {};
  const [form] = Form.useForm();
  const {
    navigate,
    location,
    params: { projectId },
  } = useRouter();
  const [isOptionsFocused, setIsOptionsFocused] = useState(null);
  const [isMultiSelectOptionsFocused, setIsMultiSelectOptionsFocused] =
    useState(null);
  const [isDateFocused, setIsDateFocused] = useState(null);
  const [isTimeFocused, setIsTimeFocused] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [eqcLabels, setEqcLabels] = useState({});
  const registerDetailsData = location?.state?.registerDetailsData;
  const registerProperty = location?.state?.registerProperty;
  const navFilter = location?.state?.navFilter;
  const navPagination = location?.state?.navPagination;
  const registerId = registerDetailsData?.id;
  const registerName = toCamelCase(
    registerDetailsData?.name?.split(' ')?.join(''),
  );
  const projectDetails = getCurrentProjectDetail();
  const nomenclatureStatus = projectDetails?.isNomenclaturePublished;

  const [getWebProject, { data: projectData }] = useLazyQueryWithCallbacks(
    GET_SINGLE_PROJECT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        dispatch({
          type: 'SET_PROJECT_DETAILS',
          data: JSON.stringify(res?.getProject),
        });
        const index = findIndex(
          res?.getProject?.projectUsers,
          (user) => Number(user?.userId) === Number(currentUserId),
        );
        if (index !== -1) {
          const loggedInUserRole =
            res?.getProject?.projectUsers?.[index]?.roles;
          dispatch({ type: 'SET_PROJECT_ROLE', data: loggedInUserRole });
        } else {
          dispatch({ type: 'SET_PROJECT_ROLE', data: '' });
        }
      },
    },
  );

  const [createNewSetProperty, { loading }] = useMutation(
    CREATE_NEW_SET_PROPERTY,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(response) {
        const registerLogId = get(response, 'createRegisterLog.registerLogId');
        navigate(
          `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/${registerLogId}`,
          {
            replace: true,
            state: {
              registerDetailsData,
              registerId,
              registerName,
              registerLogId,
              eqcLabels,
            },
          },
        );
      },
    },
  );
  const processFormValues = (
    formValues,
    registerProperties,
    isDraft = false,
  ) => {
    // Filter out deleted fields before processing
    const activeProperties = registerProperties?.filter(
      (item) => !item?.deletedAt,
    );
    return map(activeProperties, (item) => {
      const key = sanitizeFieldName(item?.name);
      const value = formValues?.[key];

      let processedValue = value;
      let extraPayload = {};

      if (item?.type === PROPERTY_TYPES?.NOMENCLATURE) {
        if (nomenclatureStatus) {
          if (value) {
            const isObject = typeof value === 'object' && value !== null;
            processedValue = isObject ? value?.label : value;
            extraPayload = {
              refType: PROPERTY_TYPES?.NOMENCLATURE,
              refId: isObject && value?.value ? [String(value?.value)] : [],
            };
          }
        } else {
          processedValue = value;
        }
      } else if (item?.type === PROPERTY_TYPES?.EQC) {
        if (Array?.isArray(value) && value?.length > 0) {
          processedValue = eqcLabels?.[key] || '';
          extraPayload = {
            refType: PROPERTY_TYPES?.EQC,
            refId: value,
          };
        } else {
          processedValue = '';
        }
      }

      if (
        Array?.isArray(processedValue) &&
        item?.type !== PROPERTY_TYPES?.EQC
      ) {
        processedValue = processedValue?.join(',');
      } else if (item?.type === PROPERTY_TYPES?.DATE && processedValue) {
        processedValue = moment(processedValue)?.format(DEFAULTDATEFORMAT);
      } else if (item?.type === PROPERTY_TYPES?.TIME && processedValue) {
        processedValue = moment(new Date(processedValue))?.format(
          TIME_24_HOUR_FORMAT,
        );
      }
      const finalValue = processedValue ?? '';
      if (!isDraft && (finalValue === '' || finalValue === null)) {
        return null;
      }
      return {
        registerDetailId: item?.id,
        value: finalValue,
        ...extraPayload,
      };
    })?.filter(Boolean);
  };

  const onFinish = async () => {
    setIsNavigating(true);
    try {
      const formValues = Object?.fromEntries(
        Object?.entries(form?.getFieldsValue())?.map(([key, value]) => [
          key?.replace(/^0_/, ''), // Remove 0_ prefix from keys
          isString(value) ? trim(value) : value,
        ]),
      );

      const result = processFormValues(formValues, registerProperty, false);
      await createNewSetProperty({
        variables: {
          registerId,
          data: { registerDetailValues: result },
        },
      });
    } catch (error) {
      message.destroy();
      message.error('Something went wrong.');
      return error;
    }
  };

  useEffect(() => {
    if (!isNaN(Number(projectId))) {
      getWebProject({
        variables: {
          id: projectId,
        },
      });
    }
  }, [projectId]);

  const handleCancel = () => {
    form.resetFields();
    navigate(-1);
  };

  // Handle field changes and trigger auto-recalculation
  const handleFieldChange = (formFieldName, value) => {
    // Update the field value
    form?.setFieldValue(formFieldName, value);

    // Extract actual field name without 0_ prefix
    const actualFieldName = formFieldName?.replace(/^0_/, '');

    // Filter out deleted fields
    const activeRegisterProperty = registerProperty?.filter(
      (item) => !item?.deletedAt,
    );

    // Find the field that was changed
    const changedField = activeRegisterProperty?.find(
      (field) => sanitizeFieldName(field?.name) === actualFieldName,
    );

    // If the changed field is numeric, date, or time, recalculate all calculation fields
    if (
      [
        PROPERTY_TYPES.NUMERIC,
        PROPERTY_TYPES.DATE,
        PROPERTY_TYPES.TIME,
      ].includes(changedField?.type)
    ) {
      // Find all calculation fields (excluding deleted ones)
      const calculationFields = activeRegisterProperty?.filter(
        (field) => field?.type === PROPERTY_TYPES.CALCULATION,
      );

      // Recalculate each calculation field
      if (calculationFields?.length) {
        // Transform activeRegisterProperty to match tableHeaders structure
        const transformedData = map(activeRegisterProperty, (item) => ({
          ...item,
          index: sanitizeFieldName(item?.name),
        }));

        calculationFields?.forEach((calcField) => {
          const calcFieldName = `0_${sanitizeFieldName(calcField?.name)}`;
          const calculatedValue = calculateFieldValue(
            calcFieldName,
            form,
            transformedData,
          );
          form?.setFieldValue(calcFieldName, calculatedValue);
        });
      }
    }
  };

  const formValues = Form?.useWatch(null, form);

  const isValueFilled = (value) =>
    !(
      value === undefined ||
      value === null ||
      (isString(value) && trim(value) === '') ||
      (Array?.isArray(value) && value?.length === 0)
    );
  const isValueWithHyphenFilled = (value) =>
    !(
      value === undefined ||
      value === null ||
      value === '-' ||
      (isString(value) && trim(value) === '') ||
      (Array?.isArray(value) && value?.length === 0)
    );

  const isFormEmpty = formValues
    ? Object?.values(formValues)?.every(
        (value) => !isValueWithHyphenFilled(value),
      )
    : true;

  const isFormCompletelyFilled = formValues
    ? Object?.values(formValues)?.every(isValueFilled)
    : false;

  const isSaveAsDraftDisabled = isFormCompletelyFilled || isFormEmpty;

  const [saveRegisterLogToDraft, { loading: saveRegisterLogToDraftLoading }] =
    useMutation(SAVE_REGISTER_LOG_TO_DRAFT, {
      onError: () => {
        setIsNavigating(false);
      },
    });

  const handleSaveAsDraft = async () => {
    setIsNavigating(true);
    try {
      const formFieldValues = Object?.fromEntries(
        Object?.entries(form?.getFieldsValue())?.map(([key, value]) => [
          key?.replace(/^0_/, ''), // Remove 0_ prefix from keys
          isString(value) ? trim(value) : value,
        ]),
      );
      const processedDetails = processFormValues(
        formFieldValues,
        registerProperty,
        true,
      );
      const draftPayload = {
        registerId,
        data: {
          registerLogDetailEntries: {
            registerDetailValues: processedDetails,
          },
        },
      };
      return saveRegisterLogToDraft({ variables: draftPayload });
    } catch {
      setIsNavigating(false);
    }
  };

  const handleSaveDraftAndGoForward = async () => {
    try {
      const response = await handleSaveAsDraft();
      const registerLogId =
        response?.data?.createRegisterLogStatusEntry?.registerLogId;
      if (registerLogId) {
        navigate(
          `${ROUTES?.PROJECTS}/${projectId}/${TAB_KEYS?.REGISTER}/${registerId}/${registerLogId}`,
          {
            replace: true,
            state: {
              registerDetailsData,
              registerId,
              registerName,
              registerLogId,
            },
          },
        );
      }
    } catch {
      // Silent error handling
    }
  };

  const isCreateButtonDisabled = useMemo(() => {
    if (!registerProperty || registerProperty?.length === 0) return true;
    // Filter out deleted fields before validation
    const activeFields = registerProperty?.filter((item) => !item?.deletedAt);
    return activeFields?.some((item) => {
      if (item?.isRequired) {
        const key = `0_${sanitizeFieldName(item?.name)}`;
        const value = formValues?.[key];
        return !isValueFilled(value);
      }
      return false;
    });
  }, [formValues, registerProperty]);

  return (
    <div className="add-project-wrapper">
      <div className="project-details-title d-flex justify-between page-header">
        <div className="project-details-title-text d-flex align-center">
          <ProjectIcon
            onClick={() => {
              navigate(ROUTES.PROJECTS);
            }}
            className="pointer"
            id="add-logs-projects-icon"
          />
          <ForwardArrow />
          <h1
            className="pointer mr-10 text-primary"
            onClick={() => {
              navigate(`${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}`, {
                state: {
                  navFilter,
                  navPagination,
                },
              });
            }}
            id="add-logs-project-name"
          >
            {projectData?.getProject?.name}
          </h1>
          <ForwardArrow />
          <h1 className="mr-10">{registerDetailsData?.name}</h1>
        </div>
      </div>
      <div className="project-module mb-20">
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Card>
            <Flex justify="space-between" align="center">
              <h3>Add Details</h3>
              <CanPerform
                action={ALLOWED_ACTION_KEYS?.ADD_REGISTER_LOG}
                type={ALLOWED_ACTION_TYPE?.PROJECT}
              >
                <Button
                  type="primary"
                  shape="round"
                  className="save-button"
                  onClick={handleSaveDraftAndGoForward}
                  loading={saveRegisterLogToDraftLoading}
                  disabled={isSaveAsDraftDisabled}
                >
                  Save as Draft
                </Button>
              </CanPerform>
            </Flex>
            {renderPropertyUi(
              registerProperty,
              form,
              isOptionsFocused,
              setIsOptionsFocused,
              isMultiSelectOptionsFocused,
              setIsMultiSelectOptionsFocused,
              isDateFocused,
              setIsDateFocused,
              isTimeFocused,
              setIsTimeFocused,
              handleFieldChange,
              setEqcLabels,
              nomenclatureStatus,
            )}
          </Card>
          <Row justify="end" className="mt-10">
            <div className="form-buttons">
              <Button
                shape="round"
                onClick={handleCancel}
                id="add-logs-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                shape="round"
                type="primary"
                className="save-button"
                htmlType="submit"
                loading={loading}
                id="add-logs-create-btn"
                disabled={isCreateButtonDisabled}
              >
                Create
              </Button>
            </div>
          </Row>
        </Form>
      </div>
      <UnsavedChangesPrompt
        openPrompt={!isFormEmpty && !isNavigating}
        onSaveDraft={handleSaveAsDraft}
      />
    </div>
  );
};

export default AddSetsProperty;
